import { UserProfileStore } from '@ownyou/ikigai';
import { NS } from '@ownyou/shared-types';
import type {
  AdvertisingProfile,
  IdentityLayer,
  MemoryStore as IkigaiMemoryStore,
  ProfileSummary,
  UserProfile,
} from '@ownyou/ikigai';
import type { CharacterProfile } from '../../services/character-summary';
import { PredictionTracker, THRESHOLD_CONFIG } from '../../services/prediction-tracker';
import type { TimelineEvent } from '../../hooks/useProfileTimeline';

export interface YouProfileObservation {
  id: string;
  label: string;
  detail: string;
  confidence?: number;
}

export interface YouLabel {
  id: string;
  label: string;
  value?: string;
  confidence?: number;
}

export interface YouIkigaiNode {
  id: string;
  label: string;
  kind: 'interest' | 'context' | 'value' | 'directive';
  confidence?: number;
  trend?: string;
}

export interface YouReadModel {
  isReady: boolean;
  profileLoaded: boolean;
  profile: UserProfile | null;
  observations: YouProfileObservation[];
  characterSummary: CharacterProfile | null;
  timeline: TimelineEvent[];
  ikigaiNodes: YouIkigaiNode[];
  demographicLabels: YouLabel[];
}

interface StoreLike {
  get<T>(namespace: readonly string[], key: string): Promise<T | null>;
  put<T>(namespace: readonly string[], key: string, value: T): Promise<void>;
  list<T>(namespace: readonly string[], options?: { limit?: number; offset?: number; filter?: Record<string, unknown> }): Promise<{ items: T[]; hasMore?: boolean }>;
  delete?(namespace: readonly string[], key: string): Promise<boolean>;
}

type StoreListOptions = { limit?: number; offset?: number; filter?: Record<string, unknown> };

function pct(value: number | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.round(Math.max(0, Math.min(1, value)) * 100);
}

function titleCase(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function buildProfileObservations(summary: ProfileSummary | null): YouProfileObservation[] {
  if (!summary) return [];
  const observations: YouProfileObservation[] = [];

  for (const interest of summary.topInterests.slice(0, 6)) {
    observations.push({
      id: `interest-${interest.topic}`,
      label: titleCase(interest.topic),
      detail: `${titleCase(interest.trend)} interest`,
      confidence: pct(interest.strength),
    });
  }

  for (const context of summary.activeContexts.slice(0, 4)) {
    observations.push({
      id: `context-${context.context}`,
      label: titleCase(context.context),
      detail: 'Current context',
      confidence: pct(context.confidence),
    });
  }

  for (const directive of summary.directives.slice(0, 4)) {
    observations.push({
      id: `directive-${directive.condition}`,
      label: directive.condition,
      detail: directive.source === 'explicit' ? 'Preference you set' : 'Inferred preference',
      confidence: pct(directive.confidence),
    });
  }

  if (summary.dataPointCount > 0 || summary.dataSourceCount > 0) {
    observations.push({
      id: 'profile-coverage',
      label: 'Profile coverage',
      detail: `${summary.dataPointCount} signals across ${summary.dataSourceCount} source${summary.dataSourceCount === 1 ? '' : 's'}`,
      confidence: pct(summary.confidence),
    });
  }

  return observations;
}

function buildIkigaiNodes(summary: ProfileSummary | null, profile: UserProfile | null): YouIkigaiNode[] {
  const nodes: YouIkigaiNode[] = [];
  for (const interest of summary?.topInterests.slice(0, 6) ?? []) {
    nodes.push({
      id: `interest-${interest.topic}`,
      label: titleCase(interest.topic),
      kind: 'interest',
      confidence: pct(interest.strength),
      trend: interest.trend,
    });
  }

  for (const context of summary?.activeContexts.slice(0, 4) ?? []) {
    nodes.push({
      id: `context-${context.context}`,
      label: titleCase(context.context),
      kind: 'context',
      confidence: pct(context.confidence),
    });
  }

  for (const value of profile?.personality?.values.slice(0, 4) ?? []) {
    nodes.push({
      id: `value-${value.value}`,
      label: titleCase(value.value),
      kind: 'value',
      confidence: pct(value.strength),
    });
  }

  for (const directive of summary?.directives.slice(0, 3) ?? []) {
    nodes.push({
      id: `directive-${directive.condition}`,
      label: directive.condition,
      kind: 'directive',
      confidence: pct(directive.confidence),
    });
  }

  return nodes.slice(0, 12);
}

function addDemographicLabel(labels: YouLabel[], id: string, label: string, value?: string, confidence?: number): void {
  if (!value || value === 'unknown') return;
  labels.push({ id, label, value: titleCase(value), confidence: pct(confidence) });
}

function buildDemographicLabels(identity: IdentityLayer | null, advertising: AdvertisingProfile | null): YouLabel[] {
  const labels: YouLabel[] = [];
  addDemographicLabel(labels, 'age-range', 'Age range', identity?.ageRange);
  addDemographicLabel(labels, 'household', 'Household', identity?.household?.type, identity?.household?.confidence);
  addDemographicLabel(labels, 'spending-tier', 'Spending tier', identity?.economic?.spendingTier, identity?.economic?.confidence);

  for (const segment of advertising?.demographicSegments.slice(0, 5) ?? []) {
    labels.push({
      id: `demographic-${segment.taxonomyId}`,
      label: 'Profile segment',
      value: segment.name,
      confidence: pct(segment.confidence),
    });
  }

  for (const segment of advertising?.interestSegments.slice(0, 5) ?? []) {
    labels.push({
      id: `interest-segment-${segment.taxonomyId}`,
      label: 'Interest segment',
      value: segment.name,
      confidence: pct(segment.strength),
    });
  }

  return labels;
}

async function buildTimeline(store: StoreLike, userId: string, characterSummary: CharacterProfile | null): Promise<TimelineEvent[]> {
  const events: TimelineEvent[] = [];

  try {
    const tracker = new PredictionTracker(store, userId);
    const accuracies = await tracker.getAllAccuracies();
    for (const accuracy of accuracies) {
      for (const threshold of accuracy.thresholdHistory) {
        const config = THRESHOLD_CONFIG[threshold.threshold];
        events.push({
          id: `threshold-${threshold.crossedAt}-${accuracy.category}`,
          type: 'threshold_crossed',
          timestamp: threshold.crossedAt,
          headline: `${titleCase(accuracy.category)} accuracy reached ${threshold.threshold}%`,
          detail: config?.label,
          category: accuracy.category,
          tokens: threshold.tokensAwarded,
          icon: 'milestone',
        });
      }
    }
  } catch {
    // Empty prediction history is valid for a new user.
  }

  try {
    const traces = await store.list<Record<string, unknown>>(NS.pipelineTraces(userId), { limit: 10, offset: 0 });
    for (const trace of traces.items) {
      const entityDiff = trace.entityDiff as { added?: unknown[]; removed?: unknown[] } | undefined;
      const added = entityDiff?.added?.length ?? 0;
      const removed = entityDiff?.removed?.length ?? 0;
      if (added === 0 && removed === 0) continue;
      const timestamp = typeof trace.completedAt === 'number'
        ? trace.completedAt
        : typeof trace.startedAt === 'number'
          ? trace.startedAt
          : null;
      if (timestamp === null) continue;
      events.push({
        id: `pipeline-${timestamp}`,
        type: 'pipeline_milestone',
        timestamp,
        headline: `${added} interests added, ${removed} faded`,
        detail: trace.trigger === 'manual' ? 'Manual refresh' : 'Profile update',
        icon: 'pipeline',
      });
    }
  } catch {
    // Empty pipeline history is valid.
  }

  if (characterSummary?.lastUpdated) {
    events.push({
      id: `summary-${characterSummary.lastUpdated}`,
      type: 'summary_generated',
      timestamp: characterSummary.lastUpdated,
      headline: 'Character profile updated',
      icon: 'profile',
    });
  }

  return events.sort((a, b) => b.timestamp - a.timestamp).slice(0, 20);
}

function asIkigaiMemoryStore(store: StoreLike): IkigaiMemoryStore {
  return {
    get: (namespace, key) => store.get(namespace, key),
    put: (namespace, key, value) => store.put(namespace, key, value),
    list: async <T>(namespace: readonly string[], options?: StoreListOptions) => {
      const result = await store.list<T>(namespace, options);
      return { items: result.items, hasMore: Boolean(result.hasMore) };
    },
    delete: store.delete ? (namespace, key) => store.delete!(namespace, key) : undefined,
  };
}

export async function loadYouReadModel(store: StoreLike | null, userId: string): Promise<YouReadModel> {
  if (!store) {
    return {
      isReady: false,
      profileLoaded: false,
      profile: null,
      observations: [],
      characterSummary: null,
      timeline: [],
      ikigaiNodes: [],
      demographicLabels: [],
    };
  }

  const profileStore = new UserProfileStore(asIkigaiMemoryStore(store), userId);
  const profile = await profileStore.loadFullProfile();
  const summary = profile.summary;
  const characterRecord = await store.get<{ value?: unknown }>(NS.characterSummary(userId), 'current');
  const characterSummary = (characterRecord?.value ?? null) as CharacterProfile | null;
  const timeline = await buildTimeline(store, userId, characterSummary);

  return {
    isReady: true,
    profileLoaded: Boolean(summary || profile.identity || profile.interests || profile.personality || profile.advertising),
    profile,
    observations: buildProfileObservations(summary),
    characterSummary,
    timeline,
    ikigaiNodes: buildIkigaiNodes(summary, profile),
    demographicLabels: buildDemographicLabels(profile.identity, profile.advertising),
  };
}
